import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Heart, 
  Bookmark, 
  Shield, 
  Star, 
  MapPin,
  Briefcase,
  GraduationCap,
  Calendar,
  Users,
  Utensils,
  MessageCircle,
  Flag,
  Ban,
  CheckCircle,
  XCircle,
  Loader2,
  Phone,
  Eye,
  Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/vivah`;

export default function VivahProfilePage({ user }) {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');

  useEffect(() => {
    if (profileId) {
      fetchProfile();
    }
  }, [profileId]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/profile/${profileId}?token=${token}`);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Profile not found');
      navigate('/vivah/matches');
    } finally {
      setLoading(false);
    }
  };

  const toggleShortlist = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.post(`${API}/shortlist/${profileId}?token=${token}`);
      setProfile(prev => ({ ...prev, is_shortlisted: response.data.is_shortlisted }));
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to update shortlist');
    }
  };

  const expressInterest = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/interest/${profileId}?token=${token}`);
      setProfile(prev => ({ ...prev, interest_status: 'sent_pending' }));
      toast.success('Interest sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send interest');
    } finally {
      setActionLoading(false);
    }
  };

  const respondToInterest = async (action) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      // We need the interest ID - fetch it from the interests endpoint
      const interestsResponse = await axios.get(`${API}/interests/received?token=${token}&status=pending`);
      const interest = interestsResponse.data.interests?.find(i => i.profile.id === profileId);
      
      if (interest) {
        await axios.put(`${API}/interest/${interest.id}/respond?action=${action}&token=${token}`);
        setProfile(prev => ({ 
          ...prev, 
          interest_status: action === 'accept' ? 'accepted' : 'declined' 
        }));
        toast.success(`Interest ${action}ed successfully!`);
      }
    } catch (error) {
      toast.error('Failed to respond to interest');
    } finally {
      setActionLoading(false);
    }
  };

  const blockProfile = async () => {
    if (!window.confirm('Are you sure you want to block this profile? They will not be able to see or contact you.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/block/${profileId}?token=${token}`);
      toast.success('Profile blocked');
      navigate('/vivah/matches');
    } catch (error) {
      toast.error('Failed to block profile');
    }
  };

  const reportProfile = async () => {
    if (!reportReason) {
      toast.error('Please select a reason');
      return;
    }
    
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(
        `${API}/report/${profileId}?reason=${encodeURIComponent(reportReason)}&details=${encodeURIComponent(reportDetails)}&token=${token}`
      );
      toast.success('Report submitted. We will review it soon.');
      setShowReportModal(false);
      setReportReason('');
      setReportDetails('');
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    const birth = new Date(dob);
    const today = new Date();
    return today.getFullYear() - birth.getFullYear();
  };

  const heightToString = (cm) => {
    if (!cm) return '';
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet}'${inches}" (${cm} cm)`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-rose-600 animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const basic = profile.basic || {};
  const religious = profile.religious || {};
  const eduCareer = profile.education_career || {};
  const family = profile.family || {};
  const contact = profile.contact || {};
  const lifestyle = profile.lifestyle || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-600 to-amber-600 text-white py-4 px-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Profile Details</h1>
              <p className="text-rose-200 text-sm">{profile.profile_id}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleShortlist}
              className={`p-2 rounded-full transition-colors ${
                profile.is_shortlisted ? 'bg-amber-500' : 'bg-white/20 hover:bg-white/30'
              }`}
            >
              <Bookmark className={`w-5 h-5 ${profile.is_shortlisted ? 'fill-current' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Photo & Basic Info */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="relative h-72 bg-gradient-to-br from-rose-100 to-amber-100">
            {profile.profile_photo ? (
              <img 
                src={profile.profile_photo} 
                alt={basic.full_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-36 h-36 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-6xl font-bold text-rose-300">
                    {basic.full_name?.charAt(0)}
                  </span>
                </div>
              </div>
            )}
            
            {/* Badges */}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              {profile.is_verified && (
                <span className="bg-green-500 text-white text-sm px-3 py-1 rounded-full flex items-center gap-1">
                  <Shield className="w-4 h-4" /> Verified
                </span>
              )}
              {profile.is_premium && (
                <span className="bg-amber-500 text-white text-sm px-3 py-1 rounded-full flex items-center gap-1">
                  <Star className="w-4 h-4" /> Premium
                </span>
              )}
            </div>
            
            {/* Views */}
            <div className="absolute bottom-4 right-4 bg-black/50 text-white text-sm px-3 py-1 rounded-full flex items-center gap-1">
              <Eye className="w-4 h-4" /> {profile.profile_views} views
            </div>
          </div>
          
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {basic.full_name}, {calculateAge(basic.dob)}
                </h2>
                <p className="text-gray-500">{heightToString(basic.height_cm)}</p>
              </div>
              <span className="text-sm text-gray-400">{profile.profile_id}</span>
            </div>
            
            {/* Quick Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-rose-50 p-3 rounded-xl">
                <p className="text-xs text-gray-500">Religion</p>
                <p className="font-semibold text-rose-700">{religious.religion}</p>
              </div>
              <div className="bg-amber-50 p-3 rounded-xl">
                <p className="text-xs text-gray-500">Caste</p>
                <p className="font-semibold text-amber-700">{religious.caste || 'Not specified'}</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-xl">
                <p className="text-xs text-gray-500">Location</p>
                <p className="font-semibold text-blue-700">{contact.city}, {contact.state}</p>
              </div>
              <div className="bg-green-50 p-3 rounded-xl">
                <p className="text-xs text-gray-500">Marital Status</p>
                <p className="font-semibold text-green-700">{basic.marital_status}</p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              {!profile.interest_status && (
                <button
                  onClick={expressInterest}
                  disabled={actionLoading}
                  className="flex-1 py-3 bg-gradient-to-r from-rose-500 to-amber-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="express-interest-btn"
                >
                  {actionLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Heart className="w-5 h-5" />
                      Express Interest
                    </>
                  )}
                </button>
              )}
              
              {profile.interest_status === 'sent_pending' && (
                <div className="flex-1 py-3 bg-amber-100 text-amber-700 font-semibold rounded-xl flex items-center justify-center gap-2">
                  <Heart className="w-5 h-5" />
                  Interest Sent - Awaiting Response
                </div>
              )}
              
              {profile.interest_status === 'received_pending' && (
                <>
                  <button
                    onClick={() => respondToInterest('accept')}
                    disabled={actionLoading}
                    className="flex-1 py-3 bg-green-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Accept Interest
                  </button>
                  <button
                    onClick={() => respondToInterest('decline')}
                    disabled={actionLoading}
                    className="py-3 px-6 bg-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-300 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <XCircle className="w-5 h-5" />
                    Decline
                  </button>
                </>
              )}
              
              {profile.interest_status === 'accepted' && (
                <button
                  onClick={() => navigate(`/vivah/interests`)}
                  className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  Start Chat
                </button>
              )}
            </div>
          </div>
        </div>

        {/* About */}
        {profile.about_me && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-rose-500" />
              About Me
            </h3>
            <p className="text-gray-600 leading-relaxed">{profile.about_me}</p>
          </div>
        )}

        {/* Basic Details */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-rose-500" />
            Basic Details
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoItem label="Profile For" value={basic.profile_for} />
            <InfoItem label="Age" value={`${calculateAge(basic.dob)} years`} />
            <InfoItem label="Height" value={heightToString(basic.height_cm)} />
            <InfoItem label="Weight" value={basic.weight_kg ? `${basic.weight_kg} kg` : 'Not specified'} />
            <InfoItem label="Complexion" value={basic.complexion} />
            <InfoItem label="Body Type" value={basic.body_type} />
            <InfoItem label="Marital Status" value={basic.marital_status} />
            <InfoItem label="Physical Status" value={basic.physical_status} />
          </div>
        </div>

        {/* Religious Details */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-rose-500" />
            Religious Details
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoItem label="Religion" value={religious.religion} />
            <InfoItem label="Caste" value={religious.caste} />
            <InfoItem label="Sub-Caste" value={religious.sub_caste} />
            <InfoItem label="Gotra" value={religious.gotra} />
            <InfoItem label="Rashi" value={religious.rashi} />
            <InfoItem label="Nakshatra" value={religious.nakshatra} />
            <InfoItem label="Manglik" value={religious.manglik} />
            <InfoItem label="Horoscope Match" value={religious.horoscope_match_required ? 'Required' : 'Not Required'} />
          </div>
        </div>

        {/* Education & Career */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-rose-500" />
            Education & Career
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoItem label="Education" value={eduCareer.highest_education} />
            <InfoItem label="College/University" value={eduCareer.education_detail} />
            <InfoItem label="Profession" value={eduCareer.profession} />
            <InfoItem label="Company" value={eduCareer.profession_detail} />
            <InfoItem label="Annual Income" value={eduCareer.annual_income} />
            <InfoItem label="Working In" value={eduCareer.working_city} />
          </div>
        </div>

        {/* Family Details */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-rose-500" />
            Family Details
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoItem label="Father's Name" value={family.father_name} />
            <InfoItem label="Father's Occupation" value={family.father_occupation} />
            <InfoItem label="Mother's Name" value={family.mother_name} />
            <InfoItem label="Mother's Occupation" value={family.mother_occupation} />
            <InfoItem label="Family Type" value={family.family_type} />
            <InfoItem label="Family Values" value={family.family_values} />
            <InfoItem label="Family Status" value={family.family_status} />
            <InfoItem label="Brothers" value={family.brothers > 0 ? `${family.brothers} (${family.brothers_married} married)` : '0'} />
            <InfoItem label="Sisters" value={family.sisters > 0 ? `${family.sisters} (${family.sisters_married} married)` : '0'} />
            <InfoItem label="Native Place" value={family.native_place} />
          </div>
          {family.about_family && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-gray-500 mb-1">About Family</p>
              <p className="text-gray-700">{family.about_family}</p>
            </div>
          )}
        </div>

        {/* Location & Lifestyle */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-rose-500" />
            Location & Lifestyle
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoItem label="City" value={contact.city} />
            <InfoItem label="State" value={contact.state} />
            <InfoItem label="Country" value={contact.country} />
            <InfoItem label="Citizenship" value={contact.citizenship} />
            <InfoItem label="Diet" value={lifestyle.diet} />
            <InfoItem label="Smoking" value={lifestyle.smoking} />
            <InfoItem label="Drinking" value={lifestyle.drinking} />
          </div>
        </div>

        {/* Partner Preferences */}
        {profile.partner_preferences && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-500" />
              Partner Preferences
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <InfoItem 
                label="Age" 
                value={`${profile.partner_preferences.age_min} - ${profile.partner_preferences.age_max} years`} 
              />
              <InfoItem 
                label="Height" 
                value={`${profile.partner_preferences.height_min_cm} - ${profile.partner_preferences.height_max_cm} cm`} 
              />
              <InfoItem 
                label="Marital Status" 
                value={profile.partner_preferences.marital_status?.join(', ')} 
              />
              <InfoItem 
                label="Religion" 
                value={profile.partner_preferences.religion?.length > 0 ? profile.partner_preferences.religion.join(', ') : 'Any'} 
              />
              <InfoItem 
                label="Manglik" 
                value={profile.partner_preferences.manglik} 
              />
            </div>
            {profile.partner_preferences.about_partner && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500 mb-1">What I'm Looking For</p>
                <p className="text-gray-700">{profile.partner_preferences.about_partner}</p>
              </div>
            )}
          </div>
        )}

        {/* Report & Block */}
        <div className="flex justify-center gap-4 pb-8">
          <button
            onClick={() => setShowReportModal(true)}
            className="text-gray-500 hover:text-rose-600 flex items-center gap-1 text-sm"
          >
            <Flag className="w-4 h-4" />
            Report Profile
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={blockProfile}
            className="text-gray-500 hover:text-rose-600 flex items-center gap-1 text-sm"
          >
            <Ban className="w-4 h-4" />
            Block Profile
          </button>
        </div>
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Report Profile</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason *</label>
                <select
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                >
                  <option value="">Select a reason</option>
                  <option value="Fake Profile">Fake Profile</option>
                  <option value="Inappropriate Photos">Inappropriate Photos</option>
                  <option value="Harassment">Harassment</option>
                  <option value="Already Married">Already Married</option>
                  <option value="Wrong Information">Wrong Information</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Details (optional)</label>
                <textarea
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                  placeholder="Provide additional details..."
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowReportModal(false)}
                className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={reportProfile}
                className="flex-1 py-3 bg-rose-600 text-white font-medium rounded-xl hover:bg-rose-700"
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="font-medium text-gray-800">{value || 'Not specified'}</p>
    </div>
  );
}
